import { ownLedgerIncomeTotals } from "./analytics";
import {
  historyCurrentMonthKey,
  historyMonthChipClassName,
  historyMonthShortLabel,
  monthKeysForYear,
} from "./historyDateFilters";
import { ownLedgerGet } from "./ownLedgerClient";
import { WHEEL_LEDGER_CHANGED_EVENT } from "./pageBridge";
import { showToast } from "./toast";
import { esc, fmtPct, fmtUsd, fmtUsdWhole } from "./watchlistData";
import { fetchAgenticEquityQuotes } from "../api/agenticClient";
import {
  ASSUMED_PUT_POP,
  AVOIDABLE_EXTRA_OTM,
  BORDERLINE_EXTRA_OTM,
  analyzePutCycles,
  attachLaterPrices,
  availableMonths,
  availableYears,
  estimatedStrikeForPop,
  extraOtmVs68,
  fellWorseAfterClose,
  laterMovePct,
  modelWhatIf,
  popToAvoidAssignment,
  reboundVsCost,
  reportForPeriod,
  wouldAssignAtPop,
  wouldSurviveAtPop,
  type AssignClass,
  type LaterPriceSource,
  type OtmPopModel,
  type PutCycle,
  type PutOutcome,
  type WhatIfModel,
  type WheelLabReport,
} from "./wheelLabAnalysis";

const DATA_CACHE_MS = 60_000;
const TARGET_POP_KEY = "sfe-wheel-lab-target-pop";
const YEAR_KEY = "sfe-wheel-lab-year";
const MONTH_KEY = "sfe-wheel-lab-month";
const COST_PATH_KEY = "sfe-wheel-lab-cost-paths";

type TableFilter = "all" | "assigned" | "avoidable" | "close-calls";

function fmtPop(pop: number | null | undefined): string {
  if (pop == null || !Number.isFinite(pop)) return "—";
  return `${(pop * 100).toFixed(0)}%`;
}

function fmtSignedPct(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return "—";
  const pct = value * 100;
  const sign = pct > 0 ? "+" : pct < 0 ? "−" : "";
  return `${sign}${Math.abs(pct).toFixed(1)}%`;
}

function fmtSignedUsd(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return "—";
  const rounded = Math.round(value);
  if (rounded === 0) return "$0";
  const sign = rounded > 0 ? "+" : "−";
  return `${sign}$${Math.abs(rounded).toLocaleString("en-US")}`;
}

function fmtCountPct(count: number, pct: number | null): string {
  if (pct == null) return String(count);
  return `${count} · ${fmtPct(pct)}`;
}

function loadTargetPop(): number {
  try {
    const raw = Number(localStorage.getItem(TARGET_POP_KEY));
    if (Number.isFinite(raw) && raw >= 50 && raw <= 90) return raw / 100;
  } catch {
    /* ignore */
  }
  return 0.75;
}

function saveTargetPop(pop: number): void {
  try {
    localStorage.setItem(TARGET_POP_KEY, String(Math.round(pop * 100)));
  } catch {
    /* ignore */
  }
}

function loadYear(): string {
  try {
    return localStorage.getItem(YEAR_KEY) || "all";
  } catch {
    return "all";
  }
}

function saveYear(year: string): void {
  try {
    localStorage.setItem(YEAR_KEY, year);
  } catch {
    /* ignore */
  }
}

function loadMonth(): string | null {
  try {
    const raw = localStorage.getItem(MONTH_KEY);
    return raw && /^\d{4}-\d{2}$/.test(raw) ? raw : null;
  } catch {
    return null;
  }
}

function saveMonth(month: string | null): void {
  try {
    if (month) localStorage.setItem(MONTH_KEY, month);
    else localStorage.removeItem(MONTH_KEY);
  } catch {
    /* ignore */
  }
}

function loadCostPaths(): boolean {
  try {
    return localStorage.getItem(COST_PATH_KEY) === "1";
  } catch {
    return false;
  }
}

function saveCostPaths(on: boolean): void {
  try {
    localStorage.setItem(COST_PATH_KEY, on ? "1" : "0");
  } catch {
    /* ignore */
  }
}

function laterSourceLabel(source: LaterPriceSource | null): string {
  if (source === "sold") return "share sale";
  if (source === "mixed") return "sale + live quote";
  if (source === "quote") return "live quote";
  return "later mark";
}

function reboundLabel(cycle: PutCycle, cost: number | null): string {
  const rebound = reboundVsCost(cycle.laterPx, cost);
  if (rebound == null) return "No later mark";
  return rebound ? "Rebounded above cost" : "Still below cost";
}

function afterCloseLabel(cycle: PutCycle): string {
  const worse = fellWorseAfterClose(cycle);
  if (worse == null) return "No later mark";
  if (worse) return "Fell further after close";
  const later = laterMovePct(cycle);
  const close = cycle.movePct;
  if (later != null && close != null && Math.abs(later - close) < 0.005) return "Flat after close";
  return "Recovered after close";
}

function defaultMonthForYear(year: string, months: string[]): string | null {
  if (!months.length) return null;
  const current = historyCurrentMonthKey();
  if (current.startsWith(`${year}-`) && months.includes(current)) return current;
  return months[months.length - 1] ?? null;
}

function outcomeLabel(outcome: PutOutcome): string {
  if (outcome === "assigned") return "Assigned";
  if (outcome === "expired") return "Expired";
  if (outcome === "btc") return "BTC";
  return "Open";
}

function outcomeKind(outcome: PutOutcome): "asgn" | "exp" | "btc" | "sto" {
  if (outcome === "assigned") return "asgn";
  if (outcome === "expired") return "exp";
  if (outcome === "btc") return "btc";
  return "sto";
}

function outcomeBadge(outcome: PutOutcome): string {
  return `<span class="sfe-txn-kind sfe-txn-kind-${outcomeKind(outcome)}">${esc(outcomeLabel(outcome))}</span>`;
}

function classBadge(value: AssignClass | null): string {
  if (!value) return "—";
  return `<span class="sfe-txn-kind sfe-wheel-lab-class-badge sfe-wheel-lab-class-badge--${value}" title="${esc(classTip(value))}">${esc(classLabel(value))}</span>`;
}

function surviveBadge(survive: boolean | null): string {
  if (survive == null) return "n/a";
  return survive
    ? `<span class="sfe-txn-kind sfe-txn-kind-exp">Yes</span>`
    : `<span class="sfe-txn-kind sfe-txn-kind-asgn">No</span>`;
}

function classLabel(value: AssignClass | null): string {
  if (value === "avoidable") return "Avoidable";
  if (value === "borderline") return "Borderline";
  if (value === "unavoidable") return "Market";
  return "—";
}

function classTip(value: AssignClass | null): string {
  if (value === "avoidable") {
    return `Finished within ${(AVOIDABLE_EXTRA_OTM * 100).toFixed(0)}% of the strike. A slightly lower strike / higher POP would likely have expired.`;
  }
  if (value === "borderline") {
    return `Needed about ${(AVOIDABLE_EXTRA_OTM * 100).toFixed(0)}–${(BORDERLINE_EXTRA_OTM * 100).toFixed(0)}% more OTM — typically ~80% POP.`;
  }
  if (value === "unavoidable") {
    return `Finished more than ${(BORDERLINE_EXTRA_OTM * 100).toFixed(0)}% below the strike. Even 80–85% POP Wheel strikes would still have assigned.`;
  }
  return "";
}

function verdictClass(kind: WheelLabReport["verdict"]["kind"]): string {
  if (kind === "raise-pop") return "sfe-wheel-lab-verdict--raise";
  if (kind === "keep-or-lower") return "sfe-wheel-lab-verdict--keep";
  if (kind === "mixed") return "sfe-wheel-lab-verdict--mixed";
  return "sfe-wheel-lab-verdict--info";
}

function colHead(label: string, tip: string, extraClass = ""): string {
  const cls = ["sfe-data-tip-host", extraClass].filter(Boolean).join(" ");
  return `<th class="${esc(cls)}" data-tip="${esc(tip)}">${label}<span class="hint" tabindex="0" role="img" data-tip="${esc(tip)}">i</span></th>`;
}

function surviveHeadHtml(targetPop: number): string {
  const pop = fmtPop(targetPop);
  const tip = `Would this assigned put have expired if you had sold at ${pop} POP? Yes = the stock finished at or above that estimated strike. Only assigned rows are scored.`;
  return `Survive @ <span data-wheel-lab-survive-pop>${esc(pop)}</span><span class="hint" tabindex="0" role="img" data-tip="${esc(tip)}">i</span>`;
}

function renderKpi(label: string, value: string, detail?: string, detailHtml?: string): string {
  return `<div class="kpi">
    <div class="kpi-k">${esc(label)}</div>
    <div class="kpi-v">${esc(value)}</div>
    ${
      detailHtml
        ? `<div class="muted small sfe-wheel-lab-kpi-detail">${detailHtml}</div>`
        : detail
          ? `<div class="muted small">${esc(detail)}</div>`
          : ""
    }
  </div>`;
}

function renderYearChips(years: string[], selected: string): string {
  const chips = [
    `<button type="button" class="sfe-income-year-chip sfe-year-chip--all${selected === "all" ? " active" : ""}" data-wheel-lab-year="all">All</button>`,
    ...years.map((year) => {
      const current = String(new Date().getFullYear()) === year;
      const tone = current ? "sfe-year-chip--current" : "sfe-year-chip--past";
      return `<button type="button" class="sfe-income-year-chip ${tone}${selected === year ? " active" : ""}" data-wheel-lab-year="${esc(year)}">${esc(year)}</button>`;
    }),
  ];
  return `<div class="sfe-income-year-chips sfe-wheel-lab-year-chips">${chips.join("")}</div>`;
}

function renderMonthChips(year: string, months: string[], selected: string | null): string {
  const allYears = year === "all";
  const rowYear = allYears ? String(new Date().getFullYear()) : year;
  const yearNum = Number(rowYear);
  if (!Number.isFinite(yearNum)) return "";
  const chips = monthKeysForYear(yearNum).map((monthKey) => {
      const inactive = allYears || !months.includes(monthKey);
      const active = !allYears && selected === monthKey;
      const label = historyMonthShortLabel(monthKey);
      const disabledAttrs = inactive
        ? allYears
          ? ` disabled aria-disabled="true" data-tip="Pick a single year to open a month" aria-label="${esc(`${label} (month view needs a year)`)}"`
          : ` disabled aria-disabled="true" data-tip="No closed puts this month" aria-label="${esc(`${label} (no closed puts)`)}"`
        : ` aria-label="${esc(label)}"`;
      return `<button type="button" class="${historyMonthChipClassName(monthKey, active, inactive)}" data-wheel-lab-month="${monthKey}" aria-pressed="${active ? "true" : "false"}"${disabledAttrs}>${esc(label)}</button>`;
    })
    .join("");
  return `<div class="sfe-income-month-selector" role="tablist" aria-label="Month">${chips}</div>`;
}

function renderClassCards(report: WheelLabReport): string {
  const total = report.assigned.length || 1;
  const cards: Array<{ id: AssignClass; title: string; hint: string }> = [
    {
      id: "avoidable",
      title: "Higher POP would help",
      hint: `Stock finished ≤${(AVOIDABLE_EXTRA_OTM * 100).toFixed(0)}% below strike`,
    },
    {
      id: "borderline",
      title: "Need a real step-up",
      hint: `Needed ${(AVOIDABLE_EXTRA_OTM * 100).toFixed(0)}–${(BORDERLINE_EXTRA_OTM * 100).toFixed(0)}% more OTM`,
    },
    {
      id: "unavoidable",
      title: "Market moved through",
      hint: `Finished >${(BORDERLINE_EXTRA_OTM * 100).toFixed(0)}% below strike`,
    },
  ];
  return `<div class="sfe-wheel-lab-class-grid">
    ${cards
      .map((card) => {
        const count = report.assignClassCounts[card.id];
        const pct = report.assigned.length ? Math.round((count / total) * 100) : 0;
        return `<div class="sfe-wheel-lab-class-card sfe-wheel-lab-class-card--${card.id}">
          <div class="kpi-k">${esc(card.title)}</div>
          <div class="kpi-v">${count}<span class="sfe-wheel-lab-class-pct">${pct}%</span></div>
          <div class="muted small">${esc(card.hint)}</div>
        </div>`;
      })
      .join("")}
  </div>`;
}

function extraOtmBarLabel(id: string, fallback: string): string {
  if (id === "0-2") return "Barely ITM";
  if (id === "2-4") return "Close call";
  if (id === "4-6") return "Needed a step-up";
  if (id === "6-8") return "Deep";
  if (id === "8-12") return "Selloff";
  if (id === "12+") return "Crash through";
  return fallback;
}

function renderExtraOtmChart(report: WheelLabReport): string {
  const max = Math.max(1, ...report.extraOtmBuckets.map((bucket) => bucket.count));
  const assignedTotal = report.assigned.length;
  const groups = [
    {
      id: "help",
      title: "Higher POP likely expires",
      hint: "Finished just under the strike",
      ids: ["0-2", "2-4"],
    },
    {
      id: "step",
      title: "Would need ~80% POP",
      hint: "4–8% more OTM",
      ids: ["4-6", "6-8"],
    },
    {
      id: "market",
      title: "Stock fell through anyway",
      hint: "Even a safer Wheel strike assigns",
      ids: ["8-12", "12+"],
    },
  ];
  const byId = new Map(report.extraOtmBuckets.map((bucket) => [bucket.id, bucket]));
  const renderBar = (id: string, tone: string): string => {
    const bucket = byId.get(id);
    if (!bucket) return "";
    const h = Math.max(bucket.count ? 8 : 2, Math.round((bucket.count / max) * 120));
    const pct = assignedTotal ? ` · ${fmtPct(bucket.count / assignedTotal)}` : "";
    return `<div class="sfe-wheel-lab-bar">
      <div class="sfe-wheel-lab-bar-val">${bucket.count}${pct}</div>
      <div class="sfe-wheel-lab-bar-fill sfe-wheel-lab-bar-fill--${tone}" style="height:${h}px"></div>
      <div class="sfe-wheel-lab-bar-label">${esc(extraOtmBarLabel(bucket.id, bucket.label))}</div>
      <div class="sfe-wheel-lab-bar-sub">${esc(bucket.label)}</div>
    </div>`;
  };
  return `<p class="muted small sfe-wheel-lab-lead">Each assignment, by how much further OTM the strike needed to be to expire. Read left → right as “close call” to “the stock collapsed.”</p>
    <div class="sfe-wheel-lab-otm-groups" role="img" aria-label="Assigned puts by how far through the strike">
      ${groups
        .map((group) => {
          const tone = group.id === "help" ? "avoidable" : group.id === "step" ? "borderline" : "unavoidable";
          const groupCount = group.ids.reduce((sum, id) => sum + (byId.get(id)?.count ?? 0), 0);
          const groupPct = assignedTotal ? fmtPct(groupCount / assignedTotal) : "—";
          return `<div class="sfe-wheel-lab-otm-group sfe-wheel-lab-otm-group--${group.id}">
            <div class="sfe-wheel-lab-otm-group-head">
              <strong>${esc(group.title)}</strong>
              <span>${groupCount} · ${groupPct}</span>
            </div>
            <div class="muted small">${esc(group.hint)}</div>
            <div class="sfe-wheel-lab-bars">${group.ids.map((id) => renderBar(id, tone)).join("")}</div>
          </div>`;
        })
        .join("")}
    </div>`;
}

function cycleKey(cycle: PutCycle): string {
  return `${cycle.ticker}|${cycle.openDate}|${cycle.strike}|${cycle.closeDate ?? ""}|${cycle.outcome}`;
}

function findCycle(cycles: PutCycle[], key: string): PutCycle | undefined {
  return cycles.find((cycle) => cycleKey(cycle) === key);
}

function popBarPct(pop: number): number {
  return Math.max(8, Math.min(100, Math.round(pop * 100)));
}

function neededPopLabel(cycle: PutCycle, model: OtmPopModel): string {
  const avoid = popToAvoidAssignment(cycle, model);
  if (!avoid) return "—";
  if (avoid.capped === "low") return "< 50%";
  if (avoid.capped === "high") return "> 95%";
  return fmtPop(avoid.neededPop);
}

function avoidVerdict(cycle: PutCycle, model: OtmPopModel, targetPop: number): string {
  const avoid = popToAvoidAssignment(cycle, model);
  if (!avoid) return "Not enough open/close prices to estimate the POP that would have expired.";
  if (cycle.outcome !== "assigned") {
    if (avoid.capped === "low") {
      return `This path finished above the strike. It stays OTM at any typical Wheel POP.`;
    }
    return `Closed ${outcomeLabel(cycle.outcome).toLowerCase()} at ${fmtPop(cycle.pop)}${cycle.popAssumed ? " (assumed)" : ""}. The stock finished high enough that about ${fmtPop(avoid.neededPop)} POP would still have expired.`;
  }
  const slider = wouldSurviveAtPop(cycle, model, targetPop);
  const sliderBit =
    slider === true
      ? ` At the ${fmtPop(targetPop)} slider this would have expired.`
      : slider === false
        ? ` At the ${fmtPop(targetPop)} slider this still assigns.`
        : "";
  if (avoid.capped === "high") {
    return `Assigned. Close finished ${cycle.extraOtmNeeded == null ? "well" : fmtSignedPct(cycle.extraOtmNeeded)} through the strike — even ~95% POP would still assign.${sliderBit}`;
  }
  const gapPts = Math.round((avoid.neededPop - cycle.pop) * 100);
  if (gapPts <= 0) {
    return `Assigned, but the path model says about ${fmtPop(avoid.neededPop)} was enough to expire.${sliderBit}`;
  }
  return `Assigned. Needed about ${fmtPop(avoid.neededPop)} to expire; you sold ${fmtPop(cycle.pop)}${cycle.popAssumed ? " (assumed)" : ""} (${gapPts} POP pts short).${sliderBit}`;
}

function renderDotPopup(cycle: PutCycle, model: OtmPopModel, targetPop: number): string {
  const avoid = popToAvoidAssignment(cycle, model);
  const neededText = neededPopLabel(cycle, model);
  const survive = wouldSurviveAtPop(cycle, model, targetPop);
  return `<div class="sfe-wheel-lab-dot-overlay" data-wheel-lab-dot-overlay>
    <div class="sfe-wheel-lab-dot-pop" role="dialog" aria-modal="true" aria-label="${esc(cycle.ticker)} put details">
      <div class="sfe-wheel-lab-dot-pop-head">
        <div>
          <h3>${esc(cycle.ticker)} ${fmtUsd(cycle.strike)} put</h3>
          <div class="sfe-wheel-lab-dot-pop-badges">
            ${outcomeBadge(cycle.outcome)}
            ${classBadge(cycle.assignClass)}
            <span class="muted">${cycle.contracts} contract${cycle.contracts === 1 ? "" : "s"}</span>
          </div>
        </div>
        <button type="button" class="sfe-modal-close" data-wheel-lab-dot-close aria-label="Close">×</button>
      </div>
      <p class="sfe-wheel-lab-dot-verdict">${esc(avoidVerdict(cycle, model, targetPop))}</p>
      <div class="sfe-wheel-lab-pop-compare">
        <div class="sfe-wheel-lab-pop-row">
          <span>Sold POP</span>
          <strong>${fmtPop(cycle.pop)}${cycle.popAssumed ? "≈" : ""}</strong>
          <i class="sfe-wheel-lab-pop-bar sfe-wheel-lab-pop-bar--sold" style="width:${popBarPct(cycle.pop)}%"></i>
        </div>
        <div class="sfe-wheel-lab-pop-row">
          <span>POP to avoid assignment</span>
          <strong>${esc(neededText)}</strong>
          <i class="sfe-wheel-lab-pop-bar sfe-wheel-lab-pop-bar--need" style="width:${avoid ? popBarPct(avoid.neededPop) : 8}%"></i>
        </div>
        <div class="sfe-wheel-lab-pop-row">
          <span>Slider POP</span>
          <strong>${fmtPop(targetPop)}${survive == null ? "" : survive ? " · expire" : " · assign"}</strong>
          <i class="sfe-wheel-lab-pop-bar sfe-wheel-lab-pop-bar--slider" style="width:${popBarPct(targetPop)}%"></i>
        </div>
      </div>
      <dl class="sfe-wheel-lab-dot-facts">
        <div><dt>Open</dt><dd>${esc(cycle.openDate || "—")}</dd></div>
        <div><dt>Close</dt><dd>${esc(cycle.closeDate || "—")}</dd></div>
        <div><dt>Expiry</dt><dd>${esc(cycle.expDate || "—")}${cycle.dte != null ? ` · ${cycle.dte} DTE` : ""}</dd></div>
        <div><dt>Strike</dt><dd>${fmtUsd(cycle.strike)}</dd></div>
        <div><dt>Open px</dt><dd>${fmtUsd(cycle.underlyingOpen)}</dd></div>
        <div><dt>Close px</dt><dd>${fmtUsd(cycle.underlyingClose)}</dd></div>
        <div><dt>Und. cost</dt><dd>${fmtUsd(cycle.strike)}</dd></div>
        <div><dt>Later px</dt><dd>${cycle.laterPx == null ? "—" : `${fmtUsd(cycle.laterPx)}${cycle.laterSource ? ` · ${laterSourceLabel(cycle.laterSource)}` : ""}`}</dd></div>
        <div><dt>Vs cost</dt><dd>${reboundLabel(cycle, cycle.strike)}</dd></div>
        <div><dt>Buffer</dt><dd>${fmtSignedPct(cycle.otmPct)}</dd></div>
        <div><dt>Stock move</dt><dd>${fmtSignedPct(cycle.movePct)}</dd></div>
        <div><dt>After close</dt><dd>${afterCloseLabel(cycle)}</dd></div>
        <div><dt>Extra OTM to expire</dt><dd>${cycle.extraOtmNeeded == null ? "—" : fmtSignedPct(cycle.extraOtmNeeded)}</dd></div>
        <div><dt>OTM a safer strike needed</dt><dd>${avoid ? fmtSignedPct(avoid.neededOtm) : "—"}</dd></div>
        <div><dt>Premium</dt><dd>${fmtUsdWhole(cycle.premium)}</dd></div>
        <div><dt>Collateral</dt><dd>${fmtUsdWhole(cycle.collateral)}</dd></div>
      </dl>
    </div>
  </div>`;
}

function sliderPathTone(cycle: PutCycle, model: OtmPopModel, targetPop: number): "expire" | "assign" | "" {
  if (cycle.outcome === "assigned") {
    const survive = wouldSurviveAtPop(cycle, model, targetPop);
    if (survive === true) return "expire";
    if (survive === false) return "assign";
    return "";
  }
  const assign = wouldAssignAtPop(cycle, model, targetPop);
  if (assign === true) return "assign";
  if (assign === false) return "expire";
  return "";
}

const SCATTER = {
  w: 1100,
  h: 560,
  pad: { l: 64, r: 24, t: 28, b: 58 },
  xMin: -0.02,
  xMax: 0.22,
  yMin: -0.38,
  yMax: 0.22,
} as const;

function scatterLayout() {
  const { w, h, pad, xMin, xMax, yMin, yMax } = SCATTER;
  const x = (otm: number): number =>
    pad.l + ((otm - xMin) / (xMax - xMin)) * (w - pad.l - pad.r);
  const y = (move: number): number =>
    pad.t + ((yMax - move) / (yMax - yMin)) * (h - pad.t - pad.b);
  const clampY = (move: number): number => y(Math.min(yMax, Math.max(yMin, move)));
  return {
    w,
    h,
    xMin,
    xMax,
    yMin,
    yMax,
    x,
    y,
    clampY,
    x0: x(0),
    xR: x(xMax),
    yTop: pad.t,
    yBot: h - pad.b,
    yZero: y(0),
  };
}

function scatterCaption(model: OtmPopModel, targetPop: number): string {
  const extra = extraOtmVs68(model, targetPop);
  return `Zones for ${fmtPop(targetPop)} · ${fmtSignedPct(extra)} extra OTM vs this book’s 68% typical strike.`;
}

function scatterZonesInner(model: OtmPopModel, targetPop: number): string {
  const { x, y, clampY, x0, xR, xMax, yTop, yBot } = scatterLayout();
  const extra = extraOtmVs68(model, targetPop);
  const yAssign0 = clampY(-extra);
  const yAssignR = clampY(-xMax - extra);
  const yAvoid0 = clampY(-extra - AVOIDABLE_EXTRA_OTM);
  const yAvoidR = clampY(-xMax - extra - AVOIDABLE_EXTRA_OTM);
  const yActR = clampY(-xMax);
  const okY = clampY(Math.min(0.1, -extra + 0.07));
  const closeY = clampY(-extra - AVOIDABLE_EXTRA_OTM * 0.45);
  const badY = clampY(Math.max(-0.32, -extra - AVOIDABLE_EXTRA_OTM - 0.08));
  return `
    <polygon class="sfe-wheel-lab-zone sfe-wheel-lab-zone--ok" points="${x0},${yTop} ${xR},${yTop} ${xR},${yAssignR} ${x0},${yAssign0}" />
    <polygon class="sfe-wheel-lab-zone sfe-wheel-lab-zone--close" points="${x0},${yAssign0} ${xR},${yAssignR} ${xR},${yAvoidR} ${x0},${yAvoid0}" />
    <polygon class="sfe-wheel-lab-zone sfe-wheel-lab-zone--bad" points="${x0},${yAvoid0} ${xR},${yAvoidR} ${xR},${yBot} ${x0},${yBot}" />
    <path class="sfe-wheel-lab-actual-line" d="M ${x0} ${y(0)} L ${xR} ${yActR}" />
    <path class="sfe-wheel-lab-assign-line" d="M ${x0} ${yAssign0} L ${xR} ${yAssignR}" />
    <text class="sfe-wheel-lab-zone-label sfe-wheel-lab-zone-label--ok" x="${x(0.12)}" y="${okY}" text-anchor="middle">Would expire at ${fmtPop(targetPop)}</text>
    <text class="sfe-wheel-lab-zone-label sfe-wheel-lab-zone-label--close" x="${x(0.145)}" y="${closeY}" text-anchor="middle">Close calls at ${fmtPop(targetPop)}</text>
    <text class="sfe-wheel-lab-zone-label sfe-wheel-lab-zone-label--bad" x="${x(0.12)}" y="${badY}" text-anchor="middle">Would assign at ${fmtPop(targetPop)}</text>
    <text class="sfe-wheel-lab-line-label" x="${xR - 4}" y="${yAssignR - 8}" text-anchor="end">Slider strike · ${fmtPop(targetPop)}</text>
    <text class="sfe-wheel-lab-actual-label" x="${xR - 4}" y="${yActR + 14}" text-anchor="end">Actual strike</text>`;
}

function scatterPlotPoints(cycles: PutCycle[]): PutCycle[] {
  return cycles.filter(
    (cycle) =>
      cycle.outcome !== "open" &&
      cycle.otmPct != null &&
      cycle.movePct != null &&
      Math.abs(cycle.otmPct) < 0.4 &&
      Math.abs(cycle.movePct) < 0.5,
  );
}

function clampScatterMove(move: number): number {
  return Math.min(SCATTER.yMax, Math.max(SCATTER.yMin, move));
}

function costStory(cycle: PutCycle, model: OtmPopModel, targetPop: number): {
  extraAssign: boolean;
  rebound: boolean | null;
  worse: boolean | null;
  cost: number;
  verdict: string;
} {
  const extraAssign = cycle.outcome !== "assigned" && wouldAssignAtPop(cycle, model, targetPop) === true;
  const cost = extraAssign ? (estimatedStrikeForPop(cycle, model, targetPop) ?? cycle.strike) : cycle.strike;
  const rebound = reboundVsCost(cycle.laterPx, cost);
  const worse = fellWorseAfterClose(cycle);
  const verdict =
    rebound === true ? "Rebounded above cost" : worse === true ? "Fell further after close" : "Later mark";
  return { extraAssign, rebound, worse, cost, verdict };
}

function costReadoutHtml(cycle: PutCycle | null, model?: OtmPopModel, targetPop?: number): string {
  if (!cycle || !model || targetPop == null) {
    return `<span class="muted">Hover a close-dot to follow that name left → right: sold → cost → close → later.</span>`;
  }
  const story = costStory(cycle, model, targetPop);
  const later =
    cycle.laterPx == null
      ? "—"
      : `${fmtUsd(cycle.laterPx)} <span class="muted">(${esc(laterSourceLabel(cycle.laterSource))})</span>`;
  return `<strong>${esc(cycle.ticker)}</strong>
    <span class="sfe-wheel-lab-cost-step"><i class="sfe-wheel-lab-cost-o"></i> Sold ${fmtUsd(cycle.underlyingOpen)}</span>
    <span class="sfe-wheel-lab-cost-arrow" aria-hidden="true">→</span>
    <span class="sfe-wheel-lab-cost-step"><i class="sfe-wheel-lab-cost-k"></i> Cost ${fmtUsd(story.cost)}</span>
    <span class="sfe-wheel-lab-cost-arrow" aria-hidden="true">→</span>
    <span class="sfe-wheel-lab-cost-step">Close ${fmtUsd(cycle.underlyingClose)}</span>
    <span class="sfe-wheel-lab-cost-arrow" aria-hidden="true">→</span>
    <span class="sfe-wheel-lab-cost-step"><i class="sfe-wheel-lab-cost-n ${story.rebound === true ? "sfe-wheel-lab-cost-n--rebound" : story.worse === true ? "sfe-wheel-lab-cost-n--worse" : ""}"></i> Later ${later}</span>
    <span class="sfe-wheel-lab-cost-verdict${story.rebound === true ? " is-rebound" : story.worse === true ? " is-worse" : ""}">${esc(story.verdict)}</span>`;
}

function scatterCostPathSvg(cycle: PutCycle, model: OtmPopModel, targetPop: number): string {
  if (cycle.otmPct == null || cycle.movePct == null || cycle.underlyingOpen == null) return "";
  const { x, y, clampY, w } = scatterLayout();
  const cx = x(Math.min(SCATTER.xMax, Math.max(SCATTER.xMin, cycle.otmPct)));
  const yOpen = y(0);
  const yCost = clampY((cycle.strike - cycle.underlyingOpen) / cycle.underlyingOpen);
  const yClose = clampY(cycle.movePct);
  const later = laterMovePct(cycle);
  const yLater = later == null ? null : clampY(later);
  const story = costStory(cycle, model, targetPop);
  const laterClass =
    story.rebound === true
      ? "sfe-wheel-lab-cost-n--rebound"
      : story.worse === true
        ? "sfe-wheel-lab-cost-n--worse"
        : "";
  const afterClass =
    story.rebound === true
      ? " sfe-wheel-lab-cost-after--rebound"
      : story.worse === true
        ? " sfe-wheel-lab-cost-after--worse"
        : "";
  const marker =
    story.rebound === true
      ? "url(#wl-cost-arrow-rebound)"
      : story.worse === true
        ? "url(#wl-cost-arrow-worse)"
        : "url(#wl-cost-arrow)";
  const toneClass = story.extraAssign
    ? " sfe-wheel-lab-cost-path--extra"
    : cycle.outcome === "assigned"
      ? " sfe-wheel-lab-cost-path--assigned"
      : "";
  const xOpen = Math.max(SCATTER.pad.l + 10, cx - 22);
  const xCost = (xOpen + cx) / 2;
  const xLater = Math.min(w - SCATTER.pad.r - 10, cx + 26);
  const topY = Math.min(yOpen, yCost, yClose, yLater ?? yClose) - 12;
  const labelSide = cx > (SCATTER.pad.l + w - SCATTER.pad.r) / 2 + SCATTER.pad.l ? "end" : "start";
  const calloutX = labelSide === "end" ? xOpen : xLater;
  return `<g class="sfe-wheel-lab-cost-path${toneClass}" data-wheel-lab-cost-path="${esc(cycleKey(cycle))}" pointer-events="none">
    <path class="sfe-wheel-lab-cost-before" d="M ${xOpen.toFixed(1)} ${yOpen.toFixed(1)} L ${xCost.toFixed(1)} ${yCost.toFixed(1)} L ${cx.toFixed(1)} ${yClose.toFixed(1)}" />
    <circle class="sfe-wheel-lab-cost-o" cx="${xOpen.toFixed(1)}" cy="${yOpen.toFixed(1)}" r="3.4"></circle>
    <rect class="sfe-wheel-lab-cost-k" x="${(xCost - 3.3).toFixed(1)}" y="${(yCost - 3.3).toFixed(1)}" width="6.6" height="6.6" rx="1.2"></rect>
    ${
      yLater == null
        ? ""
        : `<path class="sfe-wheel-lab-cost-after${afterClass}" d="M ${cx.toFixed(1)} ${yClose.toFixed(1)} L ${xLater.toFixed(1)} ${yLater.toFixed(1)}" marker-end="${marker}" />
    <circle class="sfe-wheel-lab-cost-n ${laterClass}" cx="${xLater.toFixed(1)}" cy="${yLater.toFixed(1)}" r="3.8"></circle>`
    }
    <text class="sfe-wheel-lab-cost-callout" x="${calloutX.toFixed(1)}" y="${topY.toFixed(1)}" text-anchor="${labelSide}">${esc(cycle.ticker)} · ${esc(story.verdict)}</text>
    <text class="sfe-wheel-lab-cost-label" x="${xOpen.toFixed(1)}" y="${(yOpen - 7).toFixed(1)}" text-anchor="middle">sold ${esc(fmtUsd(cycle.underlyingOpen))}</text>
    <text class="sfe-wheel-lab-cost-label" x="${xCost.toFixed(1)}" y="${(yCost - 8).toFixed(1)}" text-anchor="middle">cost ${esc(fmtUsd(cycle.strike))}</text>
    ${
      yLater == null || cycle.laterPx == null
        ? ""
        : `<text class="sfe-wheel-lab-cost-label" x="${xLater.toFixed(1)}" y="${(yLater - 8).toFixed(1)}" text-anchor="middle">later ${esc(fmtUsd(cycle.laterPx))}</text>`
    }
  </g>`;
}

function renderScatter(cycles: PutCycle[], model: OtmPopModel, targetPop: number, showCostPaths: boolean): string {
  const points = scatterPlotPoints(cycles);
  if (points.length < 2) {
    return `<p class="muted small">Need more puts with both open and close underlying prices to plot cushion vs stock move.</p>`;
  }

  const { w, h, xMin, xMax, x, y, x0, xR, yTop, yBot, yZero } = scatterLayout();

  const costPaths = points.map((cycle) => scatterCostPathSvg(cycle, model, targetPop)).join("");
  const dots = points
    .map((cycle) => {
      const cx = x(Math.min(xMax, Math.max(xMin, cycle.otmPct!)));
      const cy = y(clampScatterMove(cycle.movePct!));
      const cls =
        cycle.outcome === "assigned"
          ? cycle.assignClass ?? "assigned"
          : cycle.outcome === "btc"
            ? "btc"
            : "expired";
      const tone = sliderPathTone(cycle, model, targetPop);
      const title = `${cycle.ticker} ${fmtUsd(cycle.strike)} · ${outcomeLabel(cycle.outcome)} · sold ${fmtPop(cycle.pop)} · need ${neededPopLabel(cycle, model)} to expire`;
      return `<g class="sfe-wheel-lab-dot-hit${tone ? ` sfe-wheel-lab-dot-hit--${tone}` : ""}" data-wheel-lab-dot="${esc(cycleKey(cycle))}" role="button" tabindex="0">
        <circle class="sfe-wheel-lab-dot-hitbox" cx="${cx.toFixed(1)}" cy="${cy.toFixed(1)}" r="12" />
        <circle class="sfe-wheel-lab-dot sfe-wheel-lab-dot--${cls}" cx="${cx.toFixed(1)}" cy="${cy.toFixed(1)}" r="5"><title>${esc(title)}</title></circle>
      </g>`;
    })
    .join("");

  const xTicks = [0, 0.1, 0.2].map((otm) => {
    const px = x(otm);
    return `<line class="sfe-wheel-lab-grid" x1="${px}" y1="${yTop}" x2="${px}" y2="${yBot}" />
      <text class="sfe-wheel-lab-axis-tick" x="${px}" y="${yBot + 16}" text-anchor="middle">${Math.round(otm * 100)}%</text>`;
  });
  const yTicks = [0.2, 0.1, 0, -0.1, -0.2, -0.3].map((move) => {
    const py = y(move);
    if (py < yTop + 4 || py > yBot - 4) return "";
    const label = `${move > 0 ? "+" : move < 0 ? "−" : ""}${Math.abs(Math.round(move * 100))}%`;
    return `<line class="sfe-wheel-lab-grid" x1="${x0}" y1="${py}" x2="${xR}" y2="${py}" />
      <text class="sfe-wheel-lab-axis-tick" x="${x0 - 8}" y="${py + 4}" text-anchor="end">${label}</text>`;
  });

  return `<div class="sfe-wheel-lab-scatter-wrap${showCostPaths ? " is-cost-paths" : ""}" data-wheel-lab-scatter>
    <div class="sfe-wheel-lab-scatter-guide">
      <strong>Click a dot</strong> for the contract. With cost paths on, <strong>hover</strong> one name to follow sold → cost → close → later.
      <span>Scroll to zoom · right-drag to pan · two-finger / shift-scroll to move. Zones follow the slider.</span>
      <label class="sfe-toggle sfe-wheel-lab-cost-toggle">
        <input type="checkbox" data-wheel-lab-cost-paths ${showCostPaths ? "checked" : ""} />
        Show und. cost path
      </label>
    </div>
    <div class="sfe-wheel-lab-scatter-chart" data-wheel-lab-scatter-chart>
      <div class="sfe-wheel-lab-scatter-tools">
        <span class="muted small" data-wheel-lab-scatter-zoom>100%</span>
        <button type="button" class="small ghost" data-wheel-lab-scatter-reset hidden>Reset view</button>
      </div>
      <svg class="sfe-wheel-lab-scatter" viewBox="0 0 ${w} ${h}" role="img" aria-label="Cushion when sold versus what the stock did. Scroll to zoom, right-drag to pan.">
        <defs>
          <clipPath id="sfe-wheel-lab-scatter-clip">
            <rect x="0" y="0" width="${w}" height="${h}" />
          </clipPath>
          <marker id="wl-cost-arrow" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="6" markerHeight="6" orient="auto">
            <path d="M 0 1.2 L 10 5 L 0 8.8 Z" fill="#a78bfa" />
          </marker>
          <marker id="wl-cost-arrow-rebound" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="6" markerHeight="6" orient="auto">
            <path d="M 0 1.2 L 10 5 L 0 8.8 Z" fill="#4ade80" />
          </marker>
          <marker id="wl-cost-arrow-worse" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="6" markerHeight="6" orient="auto">
            <path d="M 0 1.2 L 10 5 L 0 8.8 Z" fill="#f87171" />
          </marker>
        </defs>
        <g clip-path="url(#sfe-wheel-lab-scatter-clip)">
          <g data-wheel-lab-scatter-view>
            <g data-wheel-lab-scatter-zones>${scatterZonesInner(model, targetPop)}</g>
            ${xTicks.join("")}
            ${yTicks.join("")}
            <line class="sfe-wheel-lab-axis sfe-wheel-lab-axis--strong" x1="${x0}" y1="${yTop}" x2="${x0}" y2="${yBot}" />
            <line class="sfe-wheel-lab-axis sfe-wheel-lab-axis--strong" x1="${x0}" y1="${yZero}" x2="${xR}" y2="${yZero}" />
            <text class="sfe-wheel-lab-axis-label" x="${(x0 + xR) / 2}" y="${h - 8}" text-anchor="middle">Cushion when you sold → more OTM</text>
            <text class="sfe-wheel-lab-axis-label" transform="translate(16 ${(yTop + yBot) / 2}) rotate(-90)" text-anchor="middle">Stock move  ·  up is safe</text>
            <text class="sfe-wheel-lab-axis-end" x="${x0 - 8}" y="${yTop + 10}" text-anchor="end">↑</text>
            <text class="sfe-wheel-lab-axis-end" x="${x0 - 8}" y="${yBot - 2}" text-anchor="end">↓</text>
            <g data-wheel-lab-cost-paths>${costPaths}</g>
            ${dots}
          </g>
        </g>
      </svg>
      <div class="sfe-wheel-lab-cost-readout" data-wheel-lab-cost-readout>${costReadoutHtml(null)}</div>
      <p class="muted small sfe-wheel-lab-caption" data-wheel-lab-scatter-caption>${scatterCaption(model, targetPop)}</p>
      <div class="sfe-wheel-lab-legend">
        ${outcomeBadge("expired")}
        ${outcomeBadge("btc")}
        ${outcomeBadge("assigned")}
        <span><i class="sfe-wheel-lab-swatch sfe-wheel-lab-swatch--avoidable"></i> Close call</span>
        <span><i class="sfe-wheel-lab-swatch sfe-wheel-lab-swatch--borderline"></i> Needed a step-up</span>
        <span><i class="sfe-wheel-lab-swatch sfe-wheel-lab-swatch--unavoidable"></i> Fell through</span>
        <span class="sfe-wheel-lab-cost-legend"><i class="sfe-wheel-lab-cost-o"></i> Sold (left)</span>
        <span class="sfe-wheel-lab-cost-legend"><i class="sfe-wheel-lab-cost-k"></i> Cost</span>
        <span class="sfe-wheel-lab-cost-legend"><i class="sfe-wheel-lab-cost-n sfe-wheel-lab-cost-n--rebound"></i> Later rebound ↑</span>
        <span class="sfe-wheel-lab-cost-legend"><i class="sfe-wheel-lab-cost-n sfe-wheel-lab-cost-n--worse"></i> Later worse ↓</span>
      </div>
    </div>
  </div>`;
}

function pnlTone(value: number): string {
  if (value > 0.5) return "roi-strong";
  if (value < -0.5) return "sfe-neg";
  return "";
}

function renderBalanceCard(
  label: string,
  value: string,
  detail: string,
  tone: "ok" | "warn" | "bad" | "" = "",
  valueClass = "",
): string {
  return `<div class="sfe-wheel-lab-balance-card${tone ? ` sfe-wheel-lab-balance-card--${tone}` : ""}">
    <div class="kpi-k">${esc(label)}</div>
    <div class="kpi-v${valueClass ? ` ${valueClass}` : ""}">${esc(value)}</div>
    <div class="muted small">${esc(detail)}</div>
  </div>`;
}

function renderReboundCard(whatIf: WhatIfModel): string {
  if (whatIf.newAssigned <= 0) {
    return renderBalanceCard(
      "Would have rebounded",
      "—",
      whatIf.extraOtmVs68 >= 0 ? "No extra assigns vs the book" : "No extra assigns at this POP",
    );
  }
  if (!whatIf.newAssignedLaterPriced) {
    return renderBalanceCard("Would have rebounded", "n/a", "No later mark after those closes");
  }
  const pct = whatIf.newAssignedRebound / whatIf.newAssignedLaterPriced;
  const worse = whatIf.newAssignedWorse
    ? ` · ${whatIf.newAssignedWorse} fell further`
    : "";
  const tone: "ok" | "warn" | "bad" | "" =
    pct >= 0.6 ? "ok" : pct <= 0.35 ? "bad" : "warn";
  return renderBalanceCard(
    "Would have rebounded",
    fmtCountPct(whatIf.newAssignedRebound, pct),
    `${whatIf.newAssignedRebound} of ${whatIf.newAssignedLaterPriced} extra assigns later above that strike${worse}`,
    tone,
    tone === "ok" ? "roi-strong" : tone === "bad" ? "sfe-neg" : "",
  );
}

function renderWhatIfBalance(whatIf: WhatIfModel, bookIncome: number): string {
  const avoidedDetail = whatIf.pricedAssigned
    ? whatIf.extraOtmVs68 <= 0
      ? `${whatIf.pricedAssigned} actual assigns stay`
      : `${whatIf.avoided} of ${whatIf.pricedAssigned} would expire`
    : "No priced assignments";
  const extraValue = whatIf.newAssigned > 0 ? `+${whatIf.newAssignedStocks}` : "0";
  const extraDetail =
    whatIf.newAssigned > 0
      ? `${whatIf.newAssigned} puts on ${whatIf.newAssignedStocks} name${whatIf.newAssignedStocks === 1 ? "" : "s"}`
      : whatIf.extraOtmVs68 >= 0
        ? "None vs actual book"
        : "None at this POP";
  const modeledIncome = bookIncome + whatIf.premiumDelta;
  const incomeDeltaPct = Math.abs(bookIncome) > 0 ? whatIf.premiumDelta / Math.abs(bookIncome) : null;
  const profitValue = fmtUsdWhole(modeledIncome);
  const profitDetail = `Current ${fmtUsdWhole(bookIncome)} · ${fmtSignedUsd(whatIf.premiumDelta)}${
    incomeDeltaPct == null ? "" : ` (${fmtSignedPct(incomeDeltaPct)})`
  }`;
  return `${renderBalanceCard(
    "Avoided assignment",
    fmtCountPct(whatIf.avoided, whatIf.avoidedPct),
    avoidedDetail,
    whatIf.avoided ? "ok" : "",
    whatIf.avoided ? "roi-strong" : "",
  )}${renderBalanceCard(
    "Still assigned",
    fmtCountPct(whatIf.stillAssigned, whatIf.stillPct),
    whatIf.newAssigned
      ? `${whatIf.pricedAssigned} actual assigns stay · extras counted separately`
      : "Still finish below that strike",
  )}${renderBalanceCard(
    "Extra assigned",
    extraValue,
    extraDetail,
    whatIf.newAssigned ? "bad" : "",
    whatIf.newAssigned ? "sfe-neg" : "",
  )}${renderReboundCard(whatIf)}${renderBalanceCard(
    "Modeled income",
    profitValue,
    profitDetail,
    pnlTone(whatIf.pnlDelta) === "roi-strong" ? "ok" : pnlTone(whatIf.pnlDelta) === "sfe-neg" ? "bad" : "",
    pnlTone(whatIf.pnlDelta),
  )}`;
}

function renderWhatIfStats(report: WheelLabReport, whatIf: WhatIfModel): string {
  const source = report.otmModel.empirical
    ? `from this book’s typical OTM at POP (${report.otmModel.sample} priced puts)`
    : "rule of thumb (~0.45% extra OTM per POP point) until more priced trades accumulate";
  const rows = report.counterfactuals
    .map((row) => {
      const active = Math.abs(row.targetPop - whatIf.targetPop) < 0.005;
      return `<tr class="${active ? "sfe-wheel-lab-row-active" : ""}">
        <td>${fmtPop(row.targetPop)}</td>
        <td class="num">${fmtSignedPct(row.extraOtmVs68)}</td>
        <td class="num">${fmtCountPct(row.avoided, row.avoidedPct)}</td>
        <td class="num">${fmtCountPct(row.stillAssigned, row.stillPct)}</td>
        <td class="num ${pnlTone(row.premiumDelta)}">${fmtSignedUsd(row.premiumDelta)}</td>
        <td class="num ${pnlTone(row.assignMarkDelta)}">${fmtSignedUsd(row.assignMarkDelta)}</td>
      </tr>`;
    })
    .join("");

  return `
    <table class="sfe-wheel-lab-table sfe-wheel-lab-whatif-stats-table" data-sfe-no-sticky-head>
      <thead>
        <tr>
          ${colHead("Target POP", "Counterfactual POP to replay the book at. Lower POP = closer to ATM, more premium, more assignment risk.")}
          ${colHead("Extra OTM vs 68%", "How much further out-of-the-money the estimated strike is versus this book’s typical 68% strike. Negative = closer to the stock.", "num")}
          ${colHead("Avoided", "Actual assignments that would have expired at this slider POP. Zero at or below the book’s 68% typical strike — a closer strike cannot un-assign what already assigned.", "num")}
          ${colHead("Still assigned", "Actual assignments that still finish below the shifted strike. At or below 68% this is the full assigned count.", "num")}
          ${colHead("Premium Δ", "Modeled premium minus actual premium. Lower POP raises this number (short-put delta ≈ 1 − POP).", "num")}
          ${colHead("Assign mark Δ", "Stock-path mark (close − strike) × shares, not booked Wheel P/L. Avoided assigns drop the ITM mark; new assigns at a lower POP add one.", "num")}
        </tr>
      </thead>
      <tbody>${rows || `<tr><td colspan="6" class="muted">No priced assignments to replay.</td></tr>`}</tbody>
    </table>
    <p class="muted small sfe-wheel-lab-caption">Lower POP collects more premium (delta ≈ 1 − POP), so premium P/L rises as you slide left. Assignment mark is (close px − strike) × shares on the stock path only — not booked Wheel P/L. Avoided assigns drop that ITM mark; still-assigned keeps the actual strike; expired/BTC that would finish ITM at a more aggressive strike add a new mark. Extra OTM ${esc(source)}. Not a reconstructed option chain.</p>`;
}

function renderWhatIf(report: WheelLabReport, targetPop: number): string {
  const whatIf = modelWhatIf(report.closed, report.assigned, report.otmModel, targetPop);
  return `
    <div class="sfe-wheel-lab-whatif">
      <div class="sfe-wheel-lab-whatif-top">
        <div class="sfe-wheel-lab-slider-block">
          <label class="sfe-wheel-lab-slider-label" for="sfe-wheel-lab-pop-slider">
            If I had sold at
            <span class="muted" data-wheel-lab-otm-label>(${fmtSignedPct(whatIf.extraOtmVs68)} extra OTM vs this book’s 68% typical strike)</span>
          </label>
          <div class="sfe-wheel-lab-slider-row">
            <span class="sfe-wheel-lab-slider-end">50%</span>
            <input id="sfe-wheel-lab-pop-slider" type="range" min="50" max="85" step="1" value="${Math.round(targetPop * 100)}" data-wheel-lab-target-pop />
            <span class="sfe-wheel-lab-slider-end">85%</span>
            <output class="sfe-wheel-lab-slider-value" data-wheel-lab-pop-label for="sfe-wheel-lab-pop-slider">${fmtPop(targetPop)}</output>
          </div>
        </div>
        <div class="sfe-wheel-lab-balance" data-wheel-lab-whatif-cards>${renderWhatIfBalance(whatIf, report.contractIncome)}</div>
      </div>
      <div data-wheel-lab-whatif-stats>${renderWhatIfStats(report, whatIf)}</div>
    </div>`;
}

function shareOf(part: number, whole: number): number | null {
  return whole > 0 ? part / whole : null;
}

function renderMixStrip(
  items: Array<{ id: string; label: string; count: number }>,
  ariaLabel: string,
): string {
  const total = items.reduce((sum, item) => sum + item.count, 0);
  if (!total) return "";
  return `<div class="sfe-wheel-lab-mix" role="img" aria-label="${esc(ariaLabel)}">
    ${items
      .map((item) => {
        const pct = item.count / total;
        const flex = Math.max(pct, item.count ? 0.08 : 0.04);
        const tone = item.id.replace(/[^a-z0-9]/gi, "").toLowerCase() || "other";
        return `<div class="sfe-wheel-lab-mix-item sfe-wheel-lab-mix-item--${esc(tone)}" style="flex:${flex.toFixed(3)}">
          <div class="sfe-wheel-lab-mix-pct">${fmtPct(pct)}</div>
          <div class="sfe-wheel-lab-mix-bar"></div>
          <div class="sfe-wheel-lab-mix-label">${esc(item.label)} · ${item.count}</div>
        </div>`;
      })
      .join("")}
  </div>`;
}

function renderBandTable(report: WheelLabReport): string {
  const closedTotal = report.totals.closed;
  const premiumTotal = report.totals.premium;
  const rows = report.popBands
    .map((band) => {
      return `<tr>
        <td>${esc(band.label)}</td>
        <td class="num">${fmtCountPct(band.closed, shareOf(band.closed, closedTotal))}</td>
        <td class="num">${fmtCountPct(band.assigned, shareOf(band.assigned, band.closed))}</td>
        <td class="num">${fmtCountPct(band.expired, shareOf(band.expired, band.closed))}</td>
        <td class="num">${fmtCountPct(band.btc, shareOf(band.btc, band.closed))}</td>
        <td class="num">${fmtUsdWhole(band.premium)}${shareOf(band.premium, premiumTotal) != null ? ` · ${fmtPct(shareOf(band.premium, premiumTotal))}` : ""}</td>
        <td class="num">${band.yieldPct == null ? "—" : fmtPct(band.yieldPct)}</td>
        <td class="num muted">${band.assumed ? `${band.assumed} assumed` : "—"}</td>
      </tr>`;
    })
    .join("");
  return `${renderMixStrip(
    report.popBands.map((band) => ({ id: band.id, label: band.label, count: band.closed })),
    "Share of closed puts by historical POP band",
  )}
  <table class="sfe-wheel-lab-table" data-sfe-no-sticky-head>
    <thead>
      <tr>
        ${colHead("POP at open", `Probability of profit recorded when the put was sold. Missing POP is assumed ${fmtPop(ASSUMED_PUT_POP)} and lands in the 68–75% band.`)}
        ${colHead("Closed", "Closed short puts in this band, with share of the historical book.", "num")}
        ${colHead("Assigned", "Puts in this band that were assigned, with share of the band.", "num")}
        ${colHead("Expired", "Puts in this band that expired OTM, with share of the band.", "num")}
        ${colHead("BTC", "Puts in this band that were bought back, with share of the band.", "num")}
        ${colHead("Premium", "Net premium kept in this band (STO credit minus BTC debit), with share of book premium.", "num")}
        ${colHead("Premium / coll.", "Premium divided by cash collateral (strike × 100 × contracts) for this band.", "num")}
        ${colHead("Missing POP", `Closed puts in this band with no recorded POP — treated as ${fmtPop(ASSUMED_PUT_POP)}.`, "num")}
      </tr>
    </thead>
    <tbody>${rows}</tbody>
  </table>
  <p class="muted small sfe-wheel-lab-caption">Percentages on top and in Closed / Premium are share of the historical book. Assigned / Expired / BTC are share of that band. Trades without a recorded POP are placed in 68–75% at the assumed ${fmtPop(ASSUMED_PUT_POP)}.</p>`;
}

function pathDotHtml(
  kind: "s0" | "k" | "st" | "n",
  outcome: PutOutcome,
  leftPct: number,
  price: number,
  laterTone = "",
): string {
  const letter = kind === "s0" ? "O" : kind === "k" ? "K" : kind === "st" ? "C" : "N";
  const name = kind === "s0" ? "Open" : kind === "k" ? "Und. cost" : kind === "st" ? "Close" : "Later";
  const tone =
    kind === "st"
      ? ` sfe-wheel-lab-path-st--${outcome}`
      : kind === "n"
        ? ` sfe-wheel-lab-path-n${laterTone}`
        : "";
  return `<span class="sfe-wheel-lab-path-dot sfe-wheel-lab-path-${kind}${tone}" style="left:${leftPct.toFixed(1)}%" title="${esc(`${name} ${fmtUsd(price)}`)}">${letter}</span>`;
}

function pathMarkers(cycle: PutCycle): string {
  if (cycle.underlyingOpen == null || cycle.underlyingClose == null) {
    return `<span class="muted">No path</span>`;
  }
  const open = cycle.underlyingOpen;
  const strike = cycle.strike;
  const close = cycle.underlyingClose;
  const later = cycle.laterPx;
  const values = later != null ? [open, strike, close, later] : [open, strike, close];
  const min = Math.min(...values);
  const max = Math.max(...values);
  const pad = (max - min) * 0.1 || 1;
  const lo = min - pad;
  const hi = max + pad;
  const span = hi - lo || 1;
  const pos = (value: number): number => ((value - lo) / span) * 100;
  const closeName =
    cycle.outcome === "assigned" ? "Assigned" : cycle.outcome === "btc" ? "BTC" : "Expired";
  const laterBit =
    later != null
      ? `\nN = later ${fmtUsd(later)} (${laterSourceLabel(cycle.laterSource)}) · ${reboundLabel(cycle, strike)}`
      : "";
  const tip = `Blue O = open ${fmtUsd(open)}\nWhite K = und. cost ${fmtUsd(strike)}\nColored C = ${closeName.toLowerCase()} ${fmtUsd(close)}${laterBit}`;
  const laterTone =
    reboundVsCost(later, strike) === true
      ? " sfe-wheel-lab-path-n--rebound"
      : fellWorseAfterClose(cycle) === true
        ? " sfe-wheel-lab-path-n--worse"
        : "";
  return `<div class="sfe-wheel-lab-path sfe-data-tip-host" data-tip="${esc(tip)}">
    <i class="sfe-wheel-lab-path-rail"></i>
    <i class="sfe-wheel-lab-path-kline" style="left:${pos(strike).toFixed(1)}%"></i>
    ${pathDotHtml("s0", cycle.outcome, pos(open), open)}
    ${pathDotHtml("k", cycle.outcome, pos(strike), strike)}
    ${pathDotHtml("st", cycle.outcome, pos(close), close)}
    ${later != null ? pathDotHtml("n", cycle.outcome, pos(later), later, laterTone) : ""}
  </div>`;
}

function renderPathLegend(): string {
  return `<div class="sfe-wheel-lab-path-legend" aria-label="Path color key">
    <span><i class="sfe-wheel-lab-path-dot sfe-wheel-lab-path-s0">O</i> Open</span>
    <span><i class="sfe-wheel-lab-path-dot sfe-wheel-lab-path-k">K</i> Strike</span>
    <span><i class="sfe-wheel-lab-path-dot sfe-wheel-lab-path-st sfe-wheel-lab-path-st--expired">C</i> Close · expired</span>
    <span><i class="sfe-wheel-lab-path-dot sfe-wheel-lab-path-st sfe-wheel-lab-path-st--assigned">C</i> Close · assigned</span>
    <span><i class="sfe-wheel-lab-path-dot sfe-wheel-lab-path-st sfe-wheel-lab-path-st--btc">C</i> Close · BTC</span>
    <span><i class="sfe-wheel-lab-path-dot sfe-wheel-lab-path-n sfe-wheel-lab-path-n--rebound">N</i> Later · rebound</span>
    <span><i class="sfe-wheel-lab-path-dot sfe-wheel-lab-path-n sfe-wheel-lab-path-n--worse">N</i> Later · worse</span>
  </div>`;
}

function renderTradeTable(report: WheelLabReport, filter: TableFilter, targetPop: number): string {
  let rows = report.closed;
  if (filter === "assigned") rows = report.assigned;
  else if (filter === "avoidable") rows = report.assigned.filter((cycle) => cycle.assignClass === "avoidable");
  else if (filter === "close-calls") rows = report.closeCalls;

  const body = rows
    .slice(0, 200)
    .map((cycle) => {
      const popText = `${fmtPop(cycle.pop)}${cycle.popAssumed ? "≈" : ""}`;
      const survive = wouldSurviveAtPop(cycle, report.otmModel, targetPop);
      const surviveHtml = cycle.outcome !== "assigned" ? "—" : surviveBadge(survive);
      return `<tr class="sfe-wheel-lab-trade sfe-wheel-lab-trade--${cycle.outcome}${cycle.assignClass ? ` sfe-wheel-lab-trade--${cycle.assignClass}` : ""}">
        <td>${esc(cycle.ticker)}</td>
        <td>${esc(cycle.openDate || "—")}</td>
        <td>${esc(cycle.closeDate || "—")}</td>
        <td class="num">${fmtUsd(cycle.strike)}</td>
        <td class="num">${fmtUsd(cycle.underlyingOpen)}</td>
        <td class="num">${fmtUsd(cycle.underlyingClose)}</td>
        <td class="num">${fmtSignedPct(cycle.otmPct)}</td>
        <td class="num">${fmtSignedPct(cycle.movePct)}</td>
        <td class="num" title="${cycle.popAssumed ? `POP not recorded — assumed ${fmtPop(ASSUMED_PUT_POP)}` : "Recorded POP at open"}">${esc(popText)}</td>
        <td>${outcomeBadge(cycle.outcome)}</td>
        <td>${classBadge(cycle.assignClass)}</td>
        <td class="num">${cycle.extraOtmNeeded == null ? "—" : fmtSignedPct(cycle.extraOtmNeeded)}</td>
        <td>${surviveHtml}</td>
        <td class="num">${fmtUsdWhole(cycle.premium)}</td>
        <td>${pathMarkers(cycle)}</td>
      </tr>`;
    })
    .join("");

  return `<div class="sfe-wheel-lab-table-wrap">
    ${renderPathLegend()}
    <table class="sfe-wheel-lab-table sfe-wheel-lab-trades" data-sfe-no-sticky-head>
      <thead>
        <tr>
          ${colHead("Ticker", "Underlying symbol for the short put.")}
          ${colHead("Open", "Date the put was sold to open.")}
          ${colHead("Close", "Date the put expired, was assigned, or was bought back.")}
          ${colHead("Strike", "Put strike. Source of truth for this path study.", "num")}
          ${colHead("Open px", "Underlying price when the put was sold.", "num")}
          ${colHead("Close px", "Underlying price at expiration, assignment, or BTC.", "num")}
          ${colHead("Buffer", "How far OTM you sold: (open px − strike) ÷ open px. Higher = more cushion.", "num")}
          ${colHead("Move", "What the stock did: (close px − open px) ÷ open px. Negative = the stock fell.", "num")}
          ${colHead("POP", `Probability of profit at open. ≈ means it was not recorded and is assumed ${fmtPop(ASSUMED_PUT_POP)}.`, "num")}
          ${colHead("Outcome", "How the short put closed: expired, assigned, or bought back.")}
          ${colHead("Class", "For assignments only. Close call = finished just under the strike. Fell through = the stock dropped well past it.")}
          ${colHead("Extra OTM", "How much further OTM the strike needed to be to expire: (strike − close px) ÷ open px. Blank if it expired.", "num")}
          <th class="sfe-data-tip-host" data-wheel-lab-survive-head data-tip="Would this assigned put have expired at the slider POP?">${surviveHeadHtml(targetPop)}</th>
          ${colHead("Premium", "Net premium kept on this cycle (STO credit minus BTC debit).", "num")}
          ${colHead("Path", "Price path on one line. Blue O = open, white K = und. cost (strike), colored C = close, N = later sale or live quote.")}
        </tr>
      </thead>
      <tbody>${body || `<tr><td colspan="15" class="muted">No puts in this filter.</td></tr>`}</tbody>
    </table>
  </div>
  <p class="muted small sfe-wheel-lab-caption">Hover a column (i) for the definition. ≈ on POP means the contract predates capture and is treated as ${fmtPop(ASSUMED_PUT_POP)}.</p>`;
}

function renderBody(
  report: WheelLabReport,
  year: string,
  years: string[],
  months: string[],
  month: string | null,
  targetPop: number,
  tableFilter: TableFilter,
  showCostPaths: boolean,
): string {
  const assumedNote = report.assumedPopCount
    ? `${report.assumedPopCount} closed put${report.assumedPopCount === 1 ? "" : "s"} had no recorded POP and are treated as ${fmtPop(ASSUMED_PUT_POP)}.`
    : "Every closed put has a recorded POP.";
  return `
    <div class="sfe-wheel-lab-toolbar" data-all-years="${year === "all" ? "true" : "false"}">
      <div class="sfe-income-year-row">
        <span class="muted small sfe-income-year-label">Year</span>
        ${renderYearChips(years, year)}
      </div>
      ${renderMonthChips(year, months, month)}
    </div>
    <section class="sfe-wheel-lab-verdict ${verdictClass(report.verdict.kind)}">
      <h3>${esc(report.verdict.title)}</h3>
      <p>${esc(report.verdict.body)}</p>
    </section>
    <div class="kpi-row sfe-inject-kpi-row sfe-wheel-lab-kpis">
      ${renderKpi("Closed short puts", String(report.totals.closed), `${report.open.length} still open`)}
      ${renderKpi(
        "Assigned",
        `${report.totals.assigned}${report.totals.assignRate != null ? ` · ${fmtPct(report.totals.assignRate)}` : ""}`,
        undefined,
        `<span class="sfe-wheel-lab-kpi-badges">${outcomeBadge("expired")} ${report.expired.length} · ${outcomeBadge("btc")} ${report.btc.length}</span>`,
      )}
      ${renderKpi("Premium kept", fmtUsdWhole(report.totals.premium), report.totals.yieldPct != null ? `${fmtPct(report.totals.yieldPct)} of collateral` : undefined)}
      ${renderKpi(`Assumed ${fmtPop(ASSUMED_PUT_POP)} POP`, String(report.assumedPopCount), assumedNote)}
      ${renderKpi("Priced paths", String(report.priced.length), report.missingPriceCount ? `${report.missingPriceCount} missing open/close px` : "Open and close underlying on file")}
    </div>
    <h4 class="insight-h">Would a higher POP have kept you out of stock?</h4>
    ${renderClassCards(report)}
    <h4 class="insight-h">How close was each assignment?</h4>
    ${renderExtraOtmChart(report)}
    <h4 class="insight-h">Did the stock stay above your strike?</h4>
    ${renderScatter(report.closed, report.otmModel, targetPop, showCostPaths)}
    <h4 class="insight-h">What if I had required a higher POP</h4>
    ${renderWhatIf(report, targetPop)}
    <h4 class="insight-h">Results by POP band</h4>
    ${renderBandTable(report)}
    <h4 class="insight-h">Put cycles</h4>
    ${renderMixStrip(
      [
        { id: "assigned", label: "Assigned", count: report.assigned.length },
        { id: "expired", label: "Expired", count: report.expired.length },
        { id: "btc", label: "BTC", count: report.btc.length },
      ],
      "Share of historical closed puts by outcome",
    )}
    <div class="sfe-wheel-lab-filters">
      ${(["all", "assigned", "avoidable", "close-calls"] as TableFilter[])
        .map((id) => {
          const closed = report.totals.closed;
          const count =
            id === "all"
              ? closed
              : id === "assigned"
                ? report.assigned.length
                : id === "avoidable"
                  ? report.assignClassCounts.avoidable
                  : report.closeCalls.length;
          const pct = id === "all" ? null : shareOf(count, closed);
          const name =
            id === "all"
              ? "All closed"
              : id === "assigned"
                ? outcomeBadge("assigned")
                : id === "avoidable"
                  ? classBadge("avoidable")
                  : "Close calls";
          const label = `${name} ${fmtCountPct(count, pct)}`;
          return `<button type="button" class="small ghost sfe-wheel-lab-filter-btn${tableFilter === id ? " active" : ""}" data-wheel-lab-filter="${id}">${label}</button>`;
        })
        .join("")}
    </div>
    ${renderTradeTable(report, tableFilter, targetPop)}
    <section class="sfe-wheel-lab-method">
      <h4 class="insight-h">How to read this</h4>
      <ul>
        <li>Unit is a <strong>closed short put</strong> (STO → expiration, assignment, or BTC) from the own ledger. Analytics rules still apply: strike is the source of truth; commissions are ignored for this path study.</li>
        <li>Missing historical POP is <strong>assumed ${fmtPop(ASSUMED_PUT_POP)}</strong>. Brokers do not publish old greeks, so this cannot be backfilled.</li>
        <li>On the path chart: <strong>right</strong> is more cushion when you sold, <strong>up</strong> is the stock rising. <strong>Scroll</strong> to zoom toward the pointer, <strong>right-drag</strong> or shift/two-finger scroll to pan, double-click empty space or Reset view to return. The <strong>bold slider strike</strong> and colored zones move with the POP slider (faint line is the actual sold strike). A green ring would expire at that POP; a red ring would assign. Turn on <strong>und. cost path</strong> and hover a close-dot: left is sold, the square is share cost, the arrow after the dot is later (sale or live quote). Green arrow = rebounded above cost; red = fell further.</li>
        <li><strong>Would have rebounded</strong> counts extra assignments at the slider whose later mark is back at or above that modeled strike. It does not invent a daily path after assignment.</li>
        <li><strong>Close call</strong> means the stock finished within ${(AVOIDABLE_EXTRA_OTM * 100).toFixed(0)}% of the strike — a modestly lower strike (higher POP) would likely have expired. <strong>Fell through</strong> means it finished more than ${(BORDERLINE_EXTRA_OTM * 100).toFixed(0)}% below, so 80%+ POP would still have assigned.</li>
        <li>The slider is a delta vs the <strong>actual book</strong>: higher POP can avoid assignments, lower POP can add them. At or below 68% avoided is 0; at or above 68% extra assigns are 0. Premium still scales by (1 − POP). Path model, not a reconstructed option chain.</li>
        <li><strong>Modeled income</strong> starts from the same Analytics trading total (STO premiums − BTC + call assignment + stock-sale P/L), then applies the put-POP premium scale for illustration. Calls and stock sales stay as booked. Est. tax is not subtracted here.</li>
      </ul>
    </section>`;
}

export class WheelLabController {
  private panel: HTMLElement | null = null;
  private rawCache: Record<string, unknown>[] = [];
  private fullReport: WheelLabReport | null = null;
  private cacheAt = 0;
  private cacheValid = false;
  private year = loadYear();
  private month = loadMonth();
  private targetPop = loadTargetPop();
  private tableFilter: TableFilter = "all";
  private showCostPaths = loadCostPaths();
  private viewReport: WheelLabReport | null = null;
  private paintedKey = "";
  private refreshGen = 0;
  private sliderBusy = false;
  private pendingQuietPaint = false;
  private sliderIdleTimer: ReturnType<typeof setTimeout> | null = null;
  private scatterScale = 1;
  private scatterPanX = 0;
  private scatterPanY = 0;
  private scatterDrag: { x: number; y: number; panX: number; panY: number } | null = null;

  private readonly onLedgerChanged = (): void => {
    if (!this.ownsPanel()) return;
    void this.refresh(true);
  };

  constructor() {
    document.addEventListener(WHEEL_LEDGER_CHANGED_EVENT, this.onLedgerChanged);
  }

  private ownsPanel(): boolean {
    return this.panel?.getAttribute("data-wheel-view") === "wheel-lab";
  }

  mount(panel: HTMLElement): void {
    this.panel = panel;
    if (!panel.querySelector(".sfe-wheel-lab-body")) {
      panel.innerHTML = `
        <div class="sfe-analytics-plus-header">
          <div class="sfe-analytics-plus-header-text">
            <h2 class="insight-h">Wheel Lab · Put POP</h2>
            <p class="muted small">Did assignments happen because 68–75% POP was too aggressive, or did the stock move through a strike you would have sold anyway?</p>
          </div>
        </div>
        <div class="sfe-wheel-lab-body">
          <p class="muted">Loading own-ledger puts…</p>
        </div>`;
    }
    this.bind();
    if (this.cacheValid && this.fullReport && !this.dashboardReady()) this.paint();
  }

  private dashboardReady(): boolean {
    return !!this.panel?.querySelector("[data-wheel-lab-whatif-cards]");
  }

  private reportKey(report: WheelLabReport): string {
    const newest = report.closed[0];
    return [
      report.totals.closed,
      report.totals.assigned,
      report.totals.premium.toFixed(2),
      report.contractIncome.toFixed(2),
      this.month ?? this.year,
      report.cycles.length,
      report.otmModel.sample,
      report.otmModel.otmAt68.toFixed(5),
      newest ? `${newest.ticker}|${newest.closeDate}|${newest.outcome}` : "",
    ].join(":");
  }

  private markRefreshing(on: boolean): void {
    this.panel?.classList.toggle("sfe-wheel-lab-refreshing", on);
    if (on) this.panel?.setAttribute("aria-busy", "true");
    else this.panel?.removeAttribute("aria-busy");
  }

  private captureView(): { scrollY: number; sliderFocus: boolean; dotKey: string | null } {
    const slider = this.panel?.querySelector("#sfe-wheel-lab-pop-slider");
    return {
      scrollY: window.scrollY,
      sliderFocus: slider instanceof HTMLElement && document.activeElement === slider,
      dotKey:
        this.panel?.querySelector(".sfe-wheel-lab-dot-hit.is-open")?.getAttribute("data-wheel-lab-dot") ??
        null,
    };
  }

  private restoreView(view: { scrollY: number; sliderFocus: boolean; dotKey: string | null }): void {
    window.scrollTo(window.scrollX, view.scrollY);
    if (view.sliderFocus) {
      this.panel
        ?.querySelector<HTMLInputElement>("#sfe-wheel-lab-pop-slider")
        ?.focus({ preventScroll: true });
    }
    if (view.dotKey) this.showDotPop(view.dotKey);
  }

  private bind(): void {
    const panel = this.panel;
    if (!panel || panel.dataset.wheelLabBound === "1") return;
    panel.dataset.wheelLabBound = "1";
    panel.addEventListener("click", (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      if (target.closest("[data-wheel-lab-dot-close]")) {
        this.hideDotPop();
        return;
      }
      const overlay = target.closest("[data-wheel-lab-dot-overlay]");
      if (overlay && target === overlay) {
        this.hideDotPop();
        return;
      }
      const dot = target.closest("[data-wheel-lab-dot]");
      const key = dot?.getAttribute("data-wheel-lab-dot");
      if (key) {
        this.showDotPop(key);
        return;
      }
      if (!(target instanceof HTMLElement)) return;
      const year = target.closest<HTMLElement>("[data-wheel-lab-year]")?.dataset.wheelLabYear;
      if (year) {
        this.year = year;
        saveYear(year);
        if (year === "all") {
          this.month = null;
        } else if (!this.month?.startsWith(`${year}-`)) {
          this.month = defaultMonthForYear(
            year,
            this.fullReport ? availableMonths(this.fullReport.cycles, year) : [],
          );
        }
        saveMonth(this.month);
        this.paint();
        return;
      }
      const month = target.closest<HTMLElement>("[data-wheel-lab-month]")?.dataset.wheelLabMonth;
      if (month) {
        if (this.year === "all") return;
        this.month = this.month === month ? null : month;
        saveMonth(this.month);
        this.paint();
        return;
      }
      if (target.closest("[data-wheel-lab-scatter-reset]")) {
        this.resetScatterView();
        return;
      }
      if (
        event.detail === 2 &&
        target.closest("[data-wheel-lab-scatter-chart]") &&
        !target.closest("[data-wheel-lab-dot]")
      ) {
        this.resetScatterView();
        return;
      }
      const filter = target.closest<HTMLElement>("[data-wheel-lab-filter]")?.dataset
        .wheelLabFilter as TableFilter | undefined;
      if (filter) {
        this.tableFilter = filter;
        this.paint();
      }
    });
    panel.addEventListener(
      "wheel",
      (event) => {
        const chart = event.target instanceof Element ? event.target.closest("[data-wheel-lab-scatter-chart]") : null;
        if (!chart) return;
        event.preventDefault();
        const panScroll = event.shiftKey || Math.abs(event.deltaX) > Math.abs(event.deltaY);
        if (panScroll) {
          if (this.scatterScale > 1) {
            this.scatterPanX -= event.shiftKey ? event.deltaY : event.deltaX;
            this.scatterPanY -= event.shiftKey ? 0 : event.deltaY;
            this.clampScatterView();
            this.applyScatterView();
          }
          return;
        }
        const svg = chart.querySelector("svg");
        if (!(svg instanceof SVGSVGElement)) return;
        const factor = event.deltaY > 0 ? 0.88 : 1.14;
        this.zoomScatterAt(svg, event.clientX, event.clientY, factor);
      },
      { passive: false },
    );
    panel.addEventListener("contextmenu", (event) => {
      if (event.target instanceof Element && event.target.closest("[data-wheel-lab-scatter-chart]")) {
        event.preventDefault();
      }
    });
    panel.addEventListener("pointerdown", (event) => {
      if (event.button !== 2) return;
      const chart = event.target instanceof Element ? event.target.closest("[data-wheel-lab-scatter-chart]") : null;
      if (!(chart instanceof HTMLElement)) return;
      event.preventDefault();
      this.scatterDrag = {
        x: event.clientX,
        y: event.clientY,
        panX: this.scatterPanX,
        panY: this.scatterPanY,
      };
      chart.classList.add("is-panning");
      chart.setPointerCapture(event.pointerId);
    });
    panel.addEventListener("pointermove", (event) => {
      if (!this.scatterDrag) return;
      this.scatterPanX = this.scatterDrag.panX + (event.clientX - this.scatterDrag.x);
      this.scatterPanY = this.scatterDrag.panY + (event.clientY - this.scatterDrag.y);
      this.clampScatterView();
      this.applyScatterView();
    });
    panel.addEventListener("pointerup", (event) => {
      if (event.button !== 2 && !this.scatterDrag) return;
      const chart = this.panel?.querySelector("[data-wheel-lab-scatter-chart]");
      chart?.classList.remove("is-panning");
      this.scatterDrag = null;
    });
    panel.addEventListener("pointercancel", () => {
      this.panel?.querySelector("[data-wheel-lab-scatter-chart]")?.classList.remove("is-panning");
      this.scatterDrag = null;
    });
    panel.addEventListener("change", (event) => {
      const target = event.target;
      if (!(target instanceof HTMLInputElement) || !target.matches("[data-wheel-lab-cost-paths]")) {
        return;
      }
      this.showCostPaths = target.checked;
      saveCostPaths(this.showCostPaths);
      this.panel?.querySelector("[data-wheel-lab-scatter]")?.classList.toggle("is-cost-paths", this.showCostPaths);
      if (!this.showCostPaths) this.focusCostPath(null);
    });
    panel.addEventListener("pointerover", (event) => {
      if (!this.showCostPaths) return;
      const target = event.target;
      if (!(target instanceof Element)) return;
      const key = target.closest("[data-wheel-lab-dot]")?.getAttribute("data-wheel-lab-dot");
      if (key) this.focusCostPath(key);
    });
    panel.addEventListener("pointerout", (event) => {
      if (!this.showCostPaths) return;
      const target = event.target;
      const next = event.relatedTarget;
      if (!(target instanceof Element)) return;
      const fromDot = target.closest("[data-wheel-lab-dot]");
      if (!fromDot) return;
      const stillOnDot =
        (next instanceof Element && fromDot.contains(next)) ||
        (next instanceof Element && next.closest("[data-wheel-lab-dot]") === fromDot);
      if (stillOnDot) return;
      const openKey = this.panel
        ?.querySelector(".sfe-wheel-lab-dot-hit.is-open")
        ?.getAttribute("data-wheel-lab-dot");
      this.focusCostPath(openKey ?? null);
    });
    panel.addEventListener("input", (event) => {
      const target = event.target;
      if (!(target instanceof HTMLInputElement) || !target.matches("[data-wheel-lab-target-pop]")) {
        return;
      }
      const value = Number(target.value);
      if (!Number.isFinite(value)) return;
      this.targetPop = value / 100;
      saveTargetPop(this.targetPop);
      this.sliderBusy = true;
      if (this.sliderIdleTimer) clearTimeout(this.sliderIdleTimer);
      this.sliderIdleTimer = setTimeout(() => {
        this.sliderBusy = false;
        if (this.pendingQuietPaint) {
          this.pendingQuietPaint = false;
          this.paint({ quiet: true });
        }
      }, 700);
      this.hideDotPop();
      this.paintWhatIf();
    });
    panel.addEventListener("pointerup", (event) => {
      const target = event.target;
      if (!(target instanceof HTMLInputElement) || !target.matches("[data-wheel-lab-target-pop]")) {
        return;
      }
      target.blur();
    });
  }

  async refresh(force = false): Promise<void> {
    const now = Date.now();
    if (!force && this.cacheValid && now - this.cacheAt < DATA_CACHE_MS && this.fullReport) {
      if (!this.dashboardReady()) this.paint();
      return;
    }
    const quiet = this.dashboardReady();
    const gen = ++this.refreshGen;
    try {
      if (!quiet) {
        const body = this.panel?.querySelector(".sfe-wheel-lab-body");
        if (body) body.innerHTML = `<p class="muted">Loading own-ledger puts…</p>`;
      } else {
        this.markRefreshing(true);
      }
      const data = await ownLedgerGet<{ transactions?: Record<string, unknown>[] }>(
        "/transactions-snapshot",
        force,
      );
      if (gen !== this.refreshGen) return;
      if (!quiet) {
        const analyzing = this.panel?.querySelector(".sfe-wheel-lab-body");
        if (analyzing) analyzing.innerHTML = `<p class="muted">Analyzing put cycles…</p>`;
        await new Promise<void>((resolve) => setTimeout(resolve, 0));
      }
      this.rawCache = data.transactions ?? [];
      this.fullReport = analyzePutCycles(this.rawCache);
      const tickers = [...new Set(this.fullReport.closed.map((cycle) => cycle.ticker))];
      const quotes = await fetchAgenticEquityQuotes(tickers).catch(() => new Map<string, number>());
      if (gen !== this.refreshGen) return;
      attachLaterPrices(this.fullReport.cycles, this.rawCache, quotes);
      const income = ownLedgerIncomeTotals(this.rawCache);
      this.fullReport.contractIncome = income.total;
      this.fullReport.contractIncomeByYear = income.byYear;
      this.fullReport.contractIncomeByMonth = income.byMonth;
      this.cacheAt = now;
      this.cacheValid = true;
      const nextKey = this.reportKey(reportForPeriod(this.fullReport, this.year, this.month));
      if (quiet && nextKey === this.paintedKey && this.dashboardReady()) return;
      if (quiet && this.sliderBusy) {
        this.pendingQuietPaint = true;
        return;
      }
      this.paint({ quiet });
    } catch (error) {
      if (gen !== this.refreshGen) return;
      const message = error instanceof Error ? error.message : "Could not load own-ledger puts.";
      if (!quiet) {
        const failed = this.panel?.querySelector(".sfe-wheel-lab-body");
        if (failed) failed.innerHTML = `<p class="muted">${esc(message)}</p>`;
      }
      if (!this.cacheValid) showToast("Could not load Wheel Lab trades.", "error");
    } finally {
      if (gen === this.refreshGen) this.markRefreshing(false);
    }
  }

  private paint(opts?: { quiet?: boolean }): void {
    if (!this.panel || !this.fullReport) return;
    const years = availableYears(this.fullReport.cycles);
    if (this.year !== "all" && !years.includes(this.year)) {
      this.year = "all";
      this.month = null;
      saveYear(this.year);
      saveMonth(this.month);
    }
    const months = this.year === "all" ? [] : availableMonths(this.fullReport.cycles, this.year);
    if (this.month && (this.year === "all" || !months.includes(this.month))) {
      this.month = this.year === "all" ? null : defaultMonthForYear(this.year, months);
      saveMonth(this.month);
    }
    this.viewReport = reportForPeriod(this.fullReport, this.year, this.month);
    const body = this.panel.querySelector(".sfe-wheel-lab-body");
    if (!body) return;
    const view = opts?.quiet ? this.captureView() : null;
    this.hideDotPop();
    try {
      body.innerHTML = renderBody(
        this.viewReport,
        this.year,
        years,
        months,
        this.month,
        this.targetPop,
        this.tableFilter,
        this.showCostPaths,
      );
      this.paintedKey = this.reportKey(this.viewReport);
      this.applyScatterView();
      if (view) this.restoreView(view);
    } catch (error) {
      const message = error instanceof Error ? error.message : "Wheel Lab failed to render.";
      body.innerHTML = `<p class="muted">${esc(message)}</p>`;
      throw error;
    }
  }

  private showDotPop(key: string): void {
    if (!this.panel || !this.viewReport) return;
    const cycle = findCycle(this.viewReport.closed, key);
    if (!cycle) return;
    this.hideDotPop();
    this.panel.querySelectorAll(".sfe-wheel-lab-dot-hit.is-open").forEach((node) => {
      node.classList.remove("is-open");
    });
    this.panel.querySelector(`[data-wheel-lab-dot="${CSS.escape(key)}"]`)?.classList.add("is-open");
    this.focusCostPath(key);
    this.panel.insertAdjacentHTML(
      "beforeend",
      renderDotPopup(cycle, this.viewReport.otmModel, this.targetPop),
    );
  }

  private focusCostPath(key: string | null): void {
    const wrap = this.panel?.querySelector("[data-wheel-lab-scatter]");
    if (!wrap) return;
    wrap.classList.toggle("is-cost-focus", !!key);
    wrap.querySelectorAll("[data-wheel-lab-cost-path]").forEach((node) => {
      node.classList.toggle("is-focus", !!key && node.getAttribute("data-wheel-lab-cost-path") === key);
    });
    wrap.querySelectorAll("[data-wheel-lab-dot]").forEach((node) => {
      node.classList.toggle("is-cost-dim", !!key && node.getAttribute("data-wheel-lab-dot") !== key);
    });
    const readout = wrap.querySelector("[data-wheel-lab-cost-readout]");
    if (!readout || !this.viewReport) return;
    const cycle = key ? findCycle(this.viewReport.closed, key) ?? null : null;
    readout.innerHTML = costReadoutHtml(cycle, this.viewReport.otmModel, this.targetPop);
  }

  private paintScatterWhatIf(): void {
    if (!this.panel || !this.viewReport) return;
    const model = this.viewReport.otmModel;
    const zones = this.panel.querySelector("[data-wheel-lab-scatter-zones]");
    if (zones) zones.innerHTML = scatterZonesInner(model, this.targetPop);
    const caption = this.panel.querySelector("[data-wheel-lab-scatter-caption]");
    if (caption) caption.textContent = scatterCaption(model, this.targetPop);
    this.panel.querySelectorAll<SVGGElement>("[data-wheel-lab-dot]").forEach((node) => {
      const key = node.getAttribute("data-wheel-lab-dot");
      if (!key) return;
      const cycle = findCycle(this.viewReport!.closed, key);
      node.classList.remove("sfe-wheel-lab-dot-hit--expire", "sfe-wheel-lab-dot-hit--assign");
      if (!cycle) return;
      const tone = sliderPathTone(cycle, model, this.targetPop);
      if (tone) node.classList.add(`sfe-wheel-lab-dot-hit--${tone}`);
    });
    this.panel.querySelectorAll<SVGGElement>("[data-wheel-lab-cost-path]").forEach((node) => {
      const key = node.getAttribute("data-wheel-lab-cost-path");
      if (!key) return;
      const cycle = findCycle(this.viewReport!.closed, key);
      node.classList.remove("sfe-wheel-lab-cost-path--extra", "sfe-wheel-lab-cost-path--assigned");
      if (!cycle) return;
      if (cycle.outcome === "assigned") node.classList.add("sfe-wheel-lab-cost-path--assigned");
      else if (wouldAssignAtPop(cycle, model, this.targetPop) === true) {
        node.classList.add("sfe-wheel-lab-cost-path--extra");
      }
    });
  }

  private svgPoint(svg: SVGSVGElement, clientX: number, clientY: number): { x: number; y: number } {
    const ctm = svg.getScreenCTM();
    if (!ctm) return { x: SCATTER.w / 2, y: SCATTER.h / 2 };
    const pt = svg.createSVGPoint();
    pt.x = clientX;
    pt.y = clientY;
    const mapped = pt.matrixTransform(ctm.inverse());
    return { x: mapped.x, y: mapped.y };
  }

  private zoomScatterAt(svg: SVGSVGElement, clientX: number, clientY: number, factor: number): void {
    const next = Math.min(8, Math.max(1, this.scatterScale * factor));
    if (next === this.scatterScale && next === 1) return;
    const pt = this.svgPoint(svg, clientX, clientY);
    const worldX = (pt.x - this.scatterPanX) / this.scatterScale;
    const worldY = (pt.y - this.scatterPanY) / this.scatterScale;
    this.scatterScale = next;
    this.scatterPanX = pt.x - worldX * next;
    this.scatterPanY = pt.y - worldY * next;
    this.clampScatterView();
    this.applyScatterView();
  }

  private clampScatterView(): void {
    if (this.scatterScale <= 1.001) {
      this.scatterScale = 1;
      this.scatterPanX = 0;
      this.scatterPanY = 0;
      return;
    }
    const maxX = SCATTER.w * (this.scatterScale - 1);
    const maxY = SCATTER.h * (this.scatterScale - 1);
    this.scatterPanX = Math.min(maxX, Math.max(-maxX, this.scatterPanX));
    this.scatterPanY = Math.min(maxY, Math.max(-maxY, this.scatterPanY));
  }

  private resetScatterView(): void {
    this.scatterScale = 1;
    this.scatterPanX = 0;
    this.scatterPanY = 0;
    this.scatterDrag = null;
    this.applyScatterView();
  }

  private applyScatterView(): void {
    const view = this.panel?.querySelector<SVGGElement>("[data-wheel-lab-scatter-view]");
    if (view) {
      view.setAttribute(
        "transform",
        `translate(${this.scatterPanX.toFixed(1)} ${this.scatterPanY.toFixed(1)}) scale(${this.scatterScale.toFixed(3)})`,
      );
    }
    const zoom = this.panel?.querySelector("[data-wheel-lab-scatter-zoom]");
    if (zoom) zoom.textContent = `${Math.round(this.scatterScale * 100)}%`;
    const reset = this.panel?.querySelector<HTMLButtonElement>("[data-wheel-lab-scatter-reset]");
    if (reset) reset.hidden = this.scatterScale <= 1.001 && !this.scatterPanX && !this.scatterPanY;
    this.panel
      ?.querySelector("[data-wheel-lab-scatter-chart]")
      ?.classList.toggle("is-zoomed", this.scatterScale > 1.001);
  }

  private hideDotPop(): void {
    this.panel?.querySelector("[data-wheel-lab-dot-overlay]")?.remove();
    this.panel?.querySelectorAll(".sfe-wheel-lab-dot-hit.is-open").forEach((node) => {
      node.classList.remove("is-open");
    });
    this.focusCostPath(null);
  }

  private paintWhatIf(): void {
    if (!this.panel || !this.viewReport || !this.ownsPanel()) return;
    const whatIf = modelWhatIf(
      this.viewReport.closed,
      this.viewReport.assigned,
      this.viewReport.otmModel,
      this.targetPop,
    );
    const popLabel = this.panel.querySelector("[data-wheel-lab-pop-label]");
    if (popLabel) popLabel.textContent = fmtPop(this.targetPop);
    const otmLabel = this.panel.querySelector("[data-wheel-lab-otm-label]");
    if (otmLabel) {
      otmLabel.textContent = `(${fmtSignedPct(whatIf.extraOtmVs68)} extra OTM vs this book’s 68% typical strike)`;
    }
    const cards = this.panel.querySelector("[data-wheel-lab-whatif-cards]");
    if (cards) cards.innerHTML = renderWhatIfBalance(whatIf, this.viewReport.contractIncome);
    this.panel.querySelectorAll<HTMLTableRowElement>(".sfe-wheel-lab-whatif-stats-table tbody tr").forEach((row) => {
      const popText = row.cells[0]?.textContent ?? "";
      row.classList.toggle("sfe-wheel-lab-row-active", popText === fmtPop(this.targetPop));
    });
    this.paintScatterWhatIf();
    this.panel.querySelectorAll<HTMLTableRowElement>(".sfe-wheel-lab-trades tbody tr").forEach((row) => {
      const surviveCell = row.cells[12];
      if (!surviveCell || surviveCell.textContent === "—") return;
      const ticker = row.cells[0]?.textContent ?? "";
      const openDate = row.cells[1]?.textContent ?? "";
      const strikeText = row.cells[3]?.textContent ?? "";
      const cycle = this.viewReport?.assigned.find(
        (item) =>
          item.ticker === ticker &&
          item.openDate === openDate &&
          fmtUsd(item.strike) === strikeText,
      );
      if (!cycle) return;
      const survive = wouldSurviveAtPop(cycle, this.viewReport!.otmModel, this.targetPop);
      surviveCell.innerHTML = surviveBadge(survive);
    });
    const survivePop = this.panel.querySelector("[data-wheel-lab-survive-pop]");
    if (survivePop) survivePop.textContent = fmtPop(this.targetPop);
    const surviveHead = this.panel.querySelector("[data-wheel-lab-survive-head]");
    if (surviveHead) {
      const tip = `Would this assigned put have expired if you had sold at ${fmtPop(this.targetPop)} POP? Yes = the stock finished at or above that estimated strike. Only assigned rows are scored.`;
      surviveHead.setAttribute("data-tip", tip);
      surviveHead.querySelector(".hint")?.setAttribute("data-tip", tip);
    }
  }
}
